// T355 — the IMAP settings that can be derived instead of typed.
//
// The operator asked whether Gmail could configure itself. OAuth cannot help
// here: `gmail.readonly` is a Google RESTRICTED scope, so an app in Testing
// status gets refresh tokens that expire every 7 days, and escaping that means
// publishing the app — which for restricted Gmail scopes requires Google's full
// verification with a security audit. For a one-person instance that is a
// weekly re-login forever, or an audit. An app password is once.
//
// What does not need OAuth is the host and the port. Both follow from the
// address, and a wrong host is the most likely thing to make a correct password
// look broken.
//
// Deliberately small. This is a convenience that fills two fields the operator
// can still overwrite — not a provider registry, and not something to grow a
// long tail into. An unknown domain returns nothing and the fields stay his.

const PROVIDERS = Object.freeze({
  'gmail.com': { host: 'imap.gmail.com', port: 993, label: 'Gmail' },
  'googlemail.com': { host: 'imap.gmail.com', port: 993, label: 'Gmail' },
  'outlook.com': { host: 'outlook.office365.com', port: 993, label: 'Outlook' },
  'hotmail.com': { host: 'outlook.office365.com', port: 993, label: 'Outlook' },
  'live.com': { host: 'outlook.office365.com', port: 993, label: 'Outlook' },
  'live.dk': { host: 'outlook.office365.com', port: 993, label: 'Outlook' },
  'icloud.com': { host: 'imap.mail.me.com', port: 993, label: 'iCloud' },
  'me.com': { host: 'imap.mail.me.com', port: 993, label: 'iCloud' },
  'yahoo.com': { host: 'imap.mail.yahoo.com', port: 993, label: 'Yahoo' },
  'yahoo.dk': { host: 'imap.mail.yahoo.com', port: 993, label: 'Yahoo' },
  'proton.me': { host: '127.0.0.1', port: 1143, label: 'Proton (needs Proton Bridge)' },
  'protonmail.com': { host: '127.0.0.1', port: 1143, label: 'Proton (needs Proton Bridge)' },
  // Denmark's own, since that is where the operator is.
  'mail.dk': { host: 'mail.dk', port: 993, label: 'Mail.dk' },
});

/**
 * IMAP host and port for an email address, or null when we do not know.
 *
 * null rather than a guess: `imap.<domain>` is right often enough to be
 * tempting and wrong often enough to send someone debugging a password that
 * was fine. Leaving the field empty says "you know this, I do not", which is
 * true.
 */
export function imapDefaultsFor(email) {
  const at = String(email ?? '').lastIndexOf('@');
  if (at < 0) return null;
  const domain = String(email).slice(at + 1).trim().toLowerCase();
  return PROVIDERS[domain] ?? null;
}

// Exported for the test that asserts every entry is well-formed — a typo in a
// host here is invisible until someone's connection fails.
export const KNOWN_IMAP_DOMAINS = Object.freeze(Object.keys(PROVIDERS));
